import promise from 'promise';

import api from 'app/shared/utils/api';
import Constants from 'app/shared/constants';
import Listings from 'app/shared/model/Listings';
import ListingConversion from 'app/shared/model/ListingConversion';
import Properties from 'app/shared/model/Properties';
import { PropertiesSelectors } from 'app/shared/selectors';

// constants
import PropertyConstants from 'app/shared/constants/PropertyConstants';

// models
import ErrorRMW from 'app/shared/model/ErrorRMW';
import ListingsCount from 'app/shared/model/ListingsCount';
import ListingInt from 'app/shared/model/ListingsInt';

// service
import appLogger from 'app/shared/loggers/appLogger';

const PropertiesActions = {
    // add boolean for restrictions here
    getListings(appendToListings = false) {
        return (dispatch, getState) => {
            const state = getState();
            const { app } = state;
            const { isSOH } = app;

            const params = PropertiesSelectors.makePropertiesFilters(state);

            return dispatch(api.users.properties.listings(params, isSOH))
                .then((response = {}) => {
                    const { error: errorRMWModel, success } = response;

                    if (response && !success && errorRMWModel) {
                        return promise.reject(errorRMWModel);
                    }

                    if (response.success && response.response) {
                        if (isSOH) {
                            const propertiesModel = Properties(response.response);

                            return dispatch(PropertiesActions.updateListings(propertiesModel, appendToListings));
                        }

                        const listingsModel = Listings(response.response);

                        return dispatch(PropertiesActions.updateListings(listingsModel, appendToListings));
                    }

                    return response;
                }).catch((errorRMWModel) => {
                    // dispatch
                    appLogger.logError({
                        message: errorRMWModel.message,
                        context: 'PropertiesActions.getListings',
                        user: getState().user,
                    });

                    return promise.reject(errorRMWModel);
                });
        };
    },
    getListingsCount() {
        return dispatch => dispatch(api.users.properties.listingsCount())
            .then((response = {}) => {
                if (response.success && response.response) {
                    const listingsCountModel = ListingsCount(response.response);
                    return dispatch(PropertiesActions.updateListingsCount(listingsCountModel));
                }
                return response;
            });
    },

    // get interstitial (intent) listings
    getIntListings(type, params) {
        return (dispatch, getState) => dispatch(api.users.properties.listingInt(type, params))
            .then((response) => {
                const { error: errorRMWModel, success } = response;

                if (response && !success && errorRMWModel) {
                    return promise.reject(errorRMWModel);
                }

                if (success && response.response) {
                    return ListingInt(response.response);
                }

                return response;
            })
            .catch((errorRMWModel) => {
                // dispatch
                appLogger.logError({
                    message: errorRMWModel.message,
                    context: 'PropertiesActions.getIntListings',
                    user: getState().user,
                });

                return promise.reject(errorRMWModel);
            });
    },

    updateCurrentProperty(payload) {
        return dispatch => dispatch({
            type: Constants.UPDATE_CURRENT_PROPERTY,
            payload,
        });
    },
    savePropertyInfo({
        fullAddress = '',
        unit = undefined,
        numUnits = '',
        propertyId = '',
        propertyTypeCode = '',
    } = {}) {
        const propertyType = PropertyConstants.PROPERTY_TYPES_BY_CODE[propertyTypeCode];

        return (dispatch, getState) => {
            const addressData = {
                fullAddress,
                propertyId,
                unitNumber: unit,
            };
            // TODO: we really need to switch to the multi endpoint for this
            return dispatch(api.properties.address.updateAddress(addressData))
                .then((response) => {
                    let { error: errorRMWModel } = response;

                    if (response && !response.success) {
                        if (!errorRMWModel) {
                            errorRMWModel = {
                                data: '',
                                code: '',
                                message: 'Something went wrong - Error is undefined',
                            };
                        }
                        // dispatch
                        appLogger.logError({
                            message: errorRMWModel.message,
                            context: 'PropertiesActions.savePropertyInfo',
                            user: getState().user,
                        });

                        return promise.reject(errorRMWModel);
                    }

                    const {
                        street, city, state, zip, lat, lon,
                    } = response.response.addressDetails;

                    const propertyTypeData = {
                        fieldName: 'propertyTypeCode',
                        propertyTypeCode,
                        propertyId,
                    };

                    return dispatch(api.users.properties.edit(propertyTypeData))
                        .then(() => {
                            if (propertyType === PropertyConstants.PROPERTY_TYPES.COMMUNITY && numUnits !== '') {
                                const dataToSave = {
                                    fieldName: 'unitsInBuilding',
                                    unitsInBuilding: numUnits,
                                    propertyId,
                                };

                                return dispatch(api.users.properties.edit(dataToSave));
                            }
                        })
                        .then(() => {
                            const updatedPropertyData = {
                                propertyType,
                                street,
                                city,
                                state,
                                zip,
                                unit,
                                geo: {
                                    lat,
                                    lon,
                                },
                                numUnits,
                            };
                            return dispatch(PropertiesActions.updateCurrentProperty(updatedPropertyData));
                        });
                })
                .catch(errorRMWModel => promise.reject(errorRMWModel));
        };
    },
    deleteSpecialOffer(queryParams) {
        return (dispatch, getState) => dispatch(api.properties.specialOffers.delete(queryParams))
            .then((response) => {
                const { error: errorRMWModel, success } = response;

                if (response && !success && errorRMWModel) {
                    return promise.reject(errorRMWModel);
                }

                return response.success;
            })
            .catch((errorRMWModel) => {
                // dispatch
                appLogger.logError({
                    message: errorRMWModel.message,
                    context: 'PropertiesActions.deleteSpecialOffer',
                    user: getState().user,
                });

                return promise.reject(errorRMWModel);
            });
    },
    createOrUpdateSpecialOffer(bodyParams, queryParams) {
        return (dispatch, getUser) => dispatch(api.properties.specialOffers.createOrUpdate(bodyParams, queryParams))
            .then((response) => {
                if (response && !response.success && response.errors) {
                    return promise.reject(response.errors);
                }

                return response.response;
            })
            .catch((errors) => {
                const errorList = [];
                const errorDisplayMap = {
                    endDate: 'End Date',
                    startDate: 'Start Date',
                    description: 'Description',
                };

                errors.forEach((error) => {
                    let name = null;
                    let message = '';

                    const { code } = error;
                    name = errorDisplayMap[error.name];
                    if (error.name === 'description') {
                        errorList.push(error.message);
                    } else {
                        errorList.push(name);
                    }
                    // dispatch each error
                    message = `Special Offer Error @ ${name} where code is ${code}`;

                    appLogger.logError({
                        message,
                        context: 'PropertiesActions.createOrUpdateSpecialOffer',
                        user: getUser.user,
                    });
                });

                // set user display message
                const errorListToString = errorList.join(', ');
                const displayMessage = `Please fix special offer errors: ${errorListToString}`;

                return promise.reject(ErrorRMW({
                    message: displayMessage,
                }));
            });
    },
    updateFilters(data) {
        return (dispatch) => {
            dispatch({
                type: Constants.UPDATE_PROPERTIES_FILTERS,
                payload: data,
            });
        };
    },
    updateListingsCount(listingsCountModel) {
        return dispatch => dispatch({
            type: Constants.UPDATE_PROPERTIES_COUNT,
            payload: listingsCountModel,
        });
    },
    updateListings(listingsModel, appendToListings = false) {
        return dispatch => dispatch({
            type: Constants.UPDATE_PROPERTIES,
            payload: {
                listingsModel,
                appendToListings,
            },
        });
    },
    getListingConversionStatus() {
        return (dispatch, getState) => dispatch(api.users.properties.feedToManual.statuses())
            .then((response = {}) => {
                const { error: errorRMWModel, success } = response;

                if (response && !success && errorRMWModel) {
                    return promise.reject(errorRMWModel);
                }

                dispatch({
                    type: Constants.UPDATE_PROPERTIES_CONVERSION,
                    payload: new ListingConversion(response),
                });
            }).catch((errorRMWModel) => {
                appLogger.logError({
                    message: errorRMWModel.message,
                    context: 'PropertiesActions.getListingConversionStatus',
                    user: getState().user,
                });
            });
    },
};

export default PropertiesActions;
